import './styles.scss'

import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from 'antd'
import Skeleton from 'react-loading-skeleton'

import FormatPrice from '@/components/FormatPrice'
import { useAppDispatch } from '@/store/hook'
import { changeCartProductReference } from '@/store/slices/cartSlice'
import usePermission from '@/hooks/usePermission'
import { MODULE_CART_CHECKOUT_RIGHTS } from '@/libs/constants/modulerights'
import { formatQuantity } from '@/libs/utils/handleNumber'

interface CartItemProps {
  isLoading?: boolean
  img: {
    src?: string
    alt: string
  }
  name: string
  materialNumber: string
  price?: number
  quantity?: number
  total?: number
  currencyUnit?: string
  productReference?: string
}

function CartItem(props: CartItemProps) {
  const {
    isLoading = false,
    img,
    name = '',
    materialNumber = '',
    price = 0,
    quantity = 0,
    total = 0,
    currencyUnit = '',
    productReference: initialProductReference = null,
  } = props

  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const [productReference, setProductReference] = useState(
    initialProductReference,
  )

  const { hasPermission: hasViewIndividualPricePermission } = usePermission(
    MODULE_CART_CHECKOUT_RIGHTS.VIEW_INDIVIDUAL_PRICE,
  )

  const timerRef = useRef(null)
  const handleChangeProductReference = (value) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    if (price === 0) {
      return
    }

    timerRef.current = setTimeout(() => {
      dispatch(
        changeCartProductReference({
          materialNumber,
          productReference: value,
          quantity,
        }),
      )
    }, 300)
  }

  const renderImage = () =>
    isLoading
      ? (
        <div className="order-overview__middle-section__cart-item__img--loading">
          <Skeleton />
        </div>
      )
      : (
        <img
          alt={img.alt}
          className="order-overview__middle-section__cart-item__img"
          src={img.src}
        />
      )

  const renderQuantity = () =>
    isLoading
      ? (
        <div className="order-overview__middle-section__cart-item__quantity order-overview__middle-section__cart-item__quantity--loading">
          <Skeleton />
        </div>
      )
      : (
        <div className="order-overview__middle-section__cart-item__quantity">
          {formatQuantity(quantity, 3)}
        </div>
      )

  const renderName = () => (
    <h3 className="order-overview__middle-section__cart-item__name">
      {isLoading ? <Skeleton /> : name}
    </h3>
  )

  const renderMaterialNumber = () => (
    <div className="order-overview__middle-section__cart-item__material-number">
      {isLoading ? <Skeleton /> : (
        `${
          t(
            'checkout.step3.middleSection.mobile.cartItem.materialNumber',
          )
        } ${materialNumber}`
      )}
    </div>
  )

  const renderPrice = () => (
    <div>
      <div className="order-overview__middle-section__cart-item__price__unit-title">
        {isLoading ? <Skeleton /> : (
          t('checkout.step3.middleSection.mobile.cartItem.price')
        )}
      </div>
      <div className="order-overview__middle-section__cart-item__price__unit">
        {isLoading
          ? <Skeleton height={15} width={50} />
          : <FormatPrice currencyUnit={currencyUnit} price={price} />}
      </div>
    </div>
  )

  const renderTotal = () => (
    <div className="order-overview__middle-section__cart-item__price__total__wrapper">
      <div className="order-overview__middle-section__cart-item__price__total-title">
        {isLoading ? <Skeleton /> : (
          t('checkout.step3.middleSection.mobile.cartItem.total')
        )}
      </div>
      <div className="order-overview__middle-section__cart-item__price__total">
        {isLoading
          ? <Skeleton height={15} width={50} />
          : <FormatPrice currencyUnit={currencyUnit} price={total} />}
      </div>
    </div>
  )

  const renderProductReference = () => (
    <div>
      <div className="order-overview__middle-section__cart-item__product-reference__title">
        {isLoading ? <Skeleton width={120} /> : (
          t(
            'checkout.step3.middleSection.mobile.cartItem.productReference.title',
          )
        )}
      </div>
      {isLoading
        ? (
          <div className="order-overview__middle-section__cart-item__product-reference__input">
            <Skeleton height={50} />
          </div>
        )
        : (
          <Input.TextArea
            className="custom-input order-overview__middle-section__cart-item__product-reference__input"
            disabled={price === 0}
            name="productReference"
            rows={2}
            value={productReference}
            placeholder={t(
              'checkout.step3.middleSection.mobile.cartItem.productReference.description',
            )}
            onFocus={(e) => e.target.select()}
            onChange={(e) => {
              if (productReference !== e.target.value) {
                setProductReference(e.target.value)
                handleChangeProductReference(e.target.value)
              }
            }}
          />
        )}
    </div>
  )

  return (
    <div className="order-overview__middle-section__cart-item__wrapper">
      <div className="order-overview__middle-section__cart-item__upper-section">
        <div className="order-overview__middle-section__cart-item__left-part">
          {renderImage()}
          {renderQuantity()}
        </div>
        <div className="order-overview__middle-section__cart-item__right-part">
          <div>
            {renderName()}
            {renderMaterialNumber()}
          </div>
          {hasViewIndividualPricePermission && (
            <div className="order-overview__middle-section__cart-item__price-section">
              {renderPrice()}
              {renderTotal()}
            </div>
          )}
        </div>
      </div>
      <div className="order-overview__middle-section__cart-item__lower-section">
        {renderProductReference()}
      </div>
    </div>
  )
}

export default CartItem
